<template>
    <div class="test-results-details">
        <b-notification v-if="error"
                        type="is-danger is-light"
                        aria-close-label="Close notification"
                        role="alert">{{ error }}</b-notification>
        <template v-else-if="!isLoading">
            <p class="title" v-if="targetBuild">{{ targetBuild.name }}</p>
            <p class="subtitle" v-if="testCase">
                {{ testCase.name }} <b-button tag="a" :href="testCase.jira_url" target="_blank" title="Open Jira"
                                              icon-left="jira" type="is-text" size="is-small" rounded/>
            </p>
            <table class="table is-striped is-fullwidth is-hoverable">
                <tbody>
                <tr>
                    <th v-for="platform in currentPlatforms" :key="platform.id">{{ platform.name }}</th>
                </tr>
                <tr>
                    <td v-for="platform in currentPlatforms" :key="`results-${platform.id}`">
                        <template v-for="testResult in resultsForPlatform(platform.id)">
                            <b-tooltip :label="testResult.mtr" :key="testResult.id" type="is-dark">
                                <router-link :to="generateTestResultLink(testResult.id)"
                                             class="button is-small is-text test-result-link"
                                             exact>
                                    <test-result :result="testResult.result"/>
                                </router-link>
                            </b-tooltip>
                        </template>
                    </td>
                </tr>
                </tbody>
            </table>
            <test-result-content v-if="currentTestResult"
                                 :test-result="currentTestResult"
                                 :test-case="testCase"
                                 :on-issue-url-update="onIssueUrlUpdate(currentTestResult.id)"/>
        </template>
        <b-loading :is-full-page="false" :active="isLoading"></b-loading>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import TestResult from "../components/TestResult";
    import TestResultContent from "../components/TestResultContent";

    export default {
        name: "TestResultsDetails",
        props: {
            testCaseId: [String, Number],
            targetBuildId: [String, Number],
            testAndRunFilters: Object,
        },
        data() {
            return {
                isLoading: false,
                error: null,
            }
        },
        created() {
            this.fetchData();
        },
        watch: {
            testCaseId() {
                this.fetchData();
            },
            targetBuildId() {
                this.fetchData();
            },
        },
        computed: {
            ...mapGetters('Platforms', [
                'currentPlatforms',
            ]),
            ...mapGetters('TargetBuilds', [
                'targetBuildById',
            ]),
            ...mapGetters('TestCases', [
                'testCaseById',
            ]),
            ...mapGetters('TestResults', [
                'currentTestResults',
                'currentTestResultById',
            ]),
            ...mapGetters('Auth', [
                'hasAccess',
            ]),
            targetBuild() {
                return this.targetBuildById(this.targetBuildId);
            },
            testCase() {
                return this.testCaseById(this.testCaseId);
            },
            currentTestResult() {
                const currentTestResultId = this.$route.query.currentTestResultId;
                if (currentTestResultId == null) return null;
                return this.currentTestResultById(currentTestResultId);
            }
        },
        methods: {
            ...mapActions('TestResults', [
                'fetchTestResultsForAllPlatforms',
                'updateIssueUrl',
            ]),
            async fetchData() {
                if (!this.hasAccess) return;
                this.isLoading = true;
                const filters = Object.assign(
                    {
                        test_case_id: this.testCaseId,
                        target_build_id: this.targetBuildId,
                    },
                    this.testAndRunFilters,
                )
                try {
                    await this.fetchTestResultsForAllPlatforms({ filters });
                } catch(message) {
                    this.error = message;
                }
                this.isLoading = false;
            },
            resultsForPlatform(platformId) {
                return this.currentTestResults.filter(item => item.platform_id === platformId);
            },
            generateTestResultLink(testResultId) {
                const query = Object.assign({}, this.$route.query, {
                    currentTestResultId: testResultId,
                });
                return { name: this.$route.name, query };
            },
            onIssueUrlUpdate(testResultId) {
                return async (issueUrl) => {
                    try {
                        await this.updateIssueUrl({ testResultId, issueUrl });
                        await this.fetchData();
                    } catch {
                        return false
                    }
                    return true
                }
            }
        },
        components: {
            TestResult,
            TestResultContent,
        },
    }
</script>

<style lang="scss" scoped>
    .test-result-link {
        opacity: 0.3;
        &.router-link-active {
            opacity: 1;
            border: 1px solid $link;
            border-radius: 50%;
        }
    }
</style>